import apiClient from './apiClient';

export interface TalentSearchFilters {
	searchText?: string;
	maxHourlyRate?: number;
	availableBefore?: string;
}

export interface TalentDetails {
	id: string;
	initials: string;
	email: string;
	hourlyRate: number;
	availableFrom: string | null;
	location: string | null;
	isAvailableForTravel: boolean;
	bioHtml: string;
	currentTitle?: string;
	skills?: string[];
	tags?: string[];
	personalStatement: string;
	whatLookingFor: string;
}

export interface TalentProfile {
	id: string;
	details: TalentDetails;
	reason: string;
}

export interface SearchResponse {
	message: string;
	profiles: TalentProfile[];
}

export interface SendTalentMessageRequest {
	subject: string;
	body: string;
}

export interface ContactMessageDto {
	id: string;
	subject: string;
	body: string;
	sentAt: string;
	talentUserId: string;
	talentFirstName: string;
	talentLastName: string;
	talentEmail: string;
	organizationUserId: string;
	organizationUserFirstName: string;
	organizationUserLastName: string;
	organizationUserEmail: string;
	organizationId: string;
	organizationName: string;
}

const talentService = {
	// Search talents with a copilot query
	searchTalentsWithCopilot: async (
		organizationId: string,
		sessionId: string,
		query: string,
	): Promise<SearchResponse> => {
		try {
			const response = await apiClient.post<SearchResponse>(
				`/api/organizations/${organizationId}/talents/copilot/sessions/${sessionId}/messages`,
				{
					message: query,
				},
			);
			return response.data;
		} catch (error) {
			console.error('Error searching talents:', error);
			throw error;
		}
	},

	// Classic search with filters
	searchTalentsWithFilters: async (
		organizationId: string,
		filters: TalentSearchFilters,
	): Promise<TalentDetails[]> => {
		try {
			const response = await apiClient.post<TalentDetails[]>(
				`/api/organizations/${organizationId}/talents/search`,
				filters,
			);
			return response.data;
		} catch (error) {
			console.error('Error searching talents with filters:', error);
			throw error;
		}
	},

	getTalentProfileById: async (
		organizationId: string,
		profileId: string,
	): Promise<TalentDetails> => {
		const response = await apiClient.get<TalentDetails>(
			`/api/organizations/${organizationId}/talents/${profileId}`,
		);
		return response.data;
	},

	sendMessageToTalent: async (
		organizationId: string,
		freelanceUserId: string,
		payload: SendTalentMessageRequest,
	): Promise<void> => {
		await apiClient.post(
			`/api/organizations/${organizationId}/talents/${freelanceUserId}/sendmessage`,
			payload,
		);
		// 204 → nessun return
	},

	getContactMessages: async (organizationId: string): Promise<ContactMessageDto[]> => {
		try {
			const response = await apiClient.get<ContactMessageDto[]>(
				`/api/organizations/${organizationId}/contactmessages`,
			);
			return response.data;
		} catch (error) {
			console.error('Error fetching contact messages:', error);
			throw error;
		}
	},

	getFreelanceContactMessages: async (): Promise<ContactMessageDto[]> => {
		try {
			const response = await apiClient.get<ContactMessageDto[]>(
				`/api/talentusers/me/contactmessages`,
			);
			return response.data;
		} catch (error) {
			console.error('Error fetching freelance contact messages:', error);
			throw error;
		}
	},

	// Generate a UUID for sessions without external libraries
	generateUUID: (): string => {
		// Implementation of RFC4122 version 4 compliant UUID generator
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	},
};

export default talentService;
